/**
 * DASHBOARD-BENCHMARK-DETAIL.WIDGET
 * Benchmark comparison for one question (histogram + question navigation), rendered inside
 * BenchmarkDetailModal. Sub-components: HistogramSkeleton, DeltaChip (file-private)
 */
import SurveyQuestionLabel from "@/components/survey-question-label"
import { useRef, useEffect, useState, useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { t as ti } from "i18next"
import { useDispatch } from "react-redux"
import { faArrowDown, faArrowUp, faEquals, faList, faChevronLeft } from "@fortawesome/free-solid-svg-icons"

import Chip from "@/components/chip"
import ListItem from "@/components/list-item"
import { AccountOptions } from "@/redux/account.types"
import { fetchBenchmarkQuestionDistribution } from "@/features/benchmark/entities/question/model/benchmarkQuestion.thunk"
import type { BenchmarkEntry } from "../model/benchmarkQuestion.context"
import type { BenchmarkDistribution } from "../model/benchmarkQuestion.types"
import Button from "@/components/button"

// ── Types ───────────────────────────────────────────────────────────

export interface BenchmarkNavItem {
  label: string
  questionAid: string
  axisLabel?: string
  liveQuestionId?: string
  topicLabel: string
  axisColor: string
  delta?: number | null
}

interface OwnProps {
  accountName: string
  accountOptions: AccountOptions
  questionAid?: string | null
  benchmarkEntry?: BenchmarkEntry | null
  score: number // raw value in the active domain's units (0-100 for "question", -100..100 for "nps")
  sector: string | null
  navItems?: BenchmarkNavItem[]
  navCurrentIndex?: number
  onNavigate?: (index: number) => void
  domain?: "question" | "nps"
  // When set (including null), skips the internal per-question distribution fetch and uses this
  // value directly — used by callers (e.g. NPS) that already have their own distribution row.
  distributionOverride?: BenchmarkDistribution | null
}

// ── Canvas ──────────────────────────────────────────────────────────

const GREY = "#8C8C8C"
const BORDER = "#E8E8E8"
const HIST_P = { t: 24, r: 12, b: 32, l: 44 } as const
const HIST_N = 10
const BAR_COLOR = "#D1D5DB"
const BAR_HOVER_COLOR = "#9CA3AF"

// Describes the raw value range a histogram covers, so the same canvas can render either a
// 0-100 scale-question distribution (shown as a 0-10 axis) or a -100..100 NPS distribution
// (shown in its own natural units) — everything below works in "raw" units and only converts
// to display text/position via this config, never via a hardcoded /10.
interface HistDomain {
  min: number
  max: number
  ticks: number[] // raw values to label on the X axis
  formatTick: (raw: number) => string
  formatValue: (raw: number) => string // "Vous X.X" / "Moy. X.X" labels
}

const QUESTION_DOMAIN: HistDomain = {
  min: 0,
  max: 100,
  ticks: [0, 20, 40, 60, 80, 100],
  formatTick: (raw) => String(raw / 10),
  formatValue: (raw) => (raw / 10).toFixed(1)
}

const NPS_DOMAIN: HistDomain = {
  min: -100,
  max: 100,
  ticks: [-100, -50, 0, 50, 100],
  formatTick: (raw) => String(raw),
  // Rounded to the integer, same precision as the NPS score card (DashboardNpsNoteWidget's
  // toFixed(0)) — avoids showing "-9.1" here next to a "-9" shown elsewhere for the same value.
  formatValue: (raw) => String(Math.round(raw))
}

// Benchmark color scale: uses account nightmare colors with fixed fallback thresholds
function benchmarkColor(score100: number, opts: AccountOptions): string {
  const s = score100 / 100
  const max = (opts.dashboardNightmareModeMax || 70) / 100
  const min = (opts.dashboardNightmareModeMin || 49) / 100
  if (s >= max) return opts.dashboardNightmareColor3 || "#20CA7E"
  if (s < min) return opts.dashboardNightmareColor1 || "#eb5a46"
  return opts.dashboardNightmareColor2 || "#f2b100"
}

function easeOutBack(x: number): number {
  const c1 = 1.70158
  const c3 = c1 + 1
  return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2)
}

function gaussianSmooth(values: number[], sigma = 2.0): number[] {
  const n = values.length
  return values.map((_, i) => {
    let sum = 0
    let weight = 0
    for (let j = 0; j < n; j++) {
      const w = Math.exp(-((i - j) ** 2) / (2 * sigma ** 2))
      sum += values[j] * w
      weight += w
    }
    return sum / weight
  })
}

// Normalize any number of source buckets (in raw domain units) into N uniform display buckets
function expandToFixed(
  dist: { v: number; n: number }[],
  N: number,
  domain: HistDomain
): { v: number; n: number }[] {
  const span = domain.max - domain.min
  const result = new Array(N).fill(0)
  if (!dist.length) return result.map((_, i) => ({ v: domain.min + (i / N) * span, n: 0 }))
  const origStep = span / dist.length
  dist.forEach((d) => {
    const s = d.v
    const e = d.v + origStep
    for (let i = 0; i < N; i++) {
      const ds = domain.min + (i / N) * span
      const de = domain.min + ((i + 1) / N) * span
      const overlap = Math.max(0, Math.min(e, de) - Math.max(s, ds))
      result[i] += d.n * (overlap / origStep)
    }
  })
  return result.map((n, i) => ({ v: domain.min + (i / N) * span, n }))
}

function paintHistogram(
  ctx: CanvasRenderingContext2D,
  W: number,
  H: number,
  rawScore: number,
  scoreColor: string,
  dist: { v: number; n: number }[],
  rawAvg: number | null,
  progress: number,
  hoverIndex: number | null = null,
  accountName = "Vous",
  domain: HistDomain = QUESTION_DOMAIN
) {
  ctx.clearRect(0, 0, W, H)

  if (!dist.length) {
    ctx.fillStyle = GREY
    ctx.font = "12px -apple-system,system-ui,sans-serif"
    ctx.textAlign = "center"
    ctx.fillText(ti("benchmark_q_no_distribution"), W / 2, H / 2)
    return
  }

  const { t: pt, r: pr, b: pb, l: pl } = HIST_P
  const displayDist = expandToFixed(dist, HIST_N, domain)
  const cw = W - pl - pr
  const ch = H - pt - pb
  const span = domain.max - domain.min
  const xPos = (raw: number) => pl + ((raw - domain.min) / span) * cw
  const smoothed = gaussianSmooth(displayDist.map((d) => d.n))
  const totalSmoothed = smoothed.reduce((a, b) => a + b, 0)
  if (!totalSmoothed) return
  const pct = smoothed.map((v) => (v / totalSmoothed) * 100)
  const maxPct = Math.max(...pct)
  const yMax = Math.ceil(maxPct / 5) * 5 || 20
  const barSlotW = cw / HIST_N
  const GAP = 2

  // Y axis: % gridlines + labels (left)
  ctx.font = "10px -apple-system,system-ui,sans-serif"
  const gridSteps = yMax <= 15 ? 3 : 4
  for (let step = 0; step <= gridSteps; step++) {
    const val = (yMax / gridSteps) * step
    const y = pt + ch - (val / yMax) * ch
    ctx.fillStyle = GREY
    ctx.textAlign = "right"
    ctx.fillText(`${Math.round(val)}%`, pl - 6, y + 4)
    if (step > 0) {
      ctx.beginPath()
      ctx.moveTo(pl, y)
      ctx.lineTo(pl + cw, y)
      ctx.strokeStyle = BORDER
      ctx.lineWidth = 0.5
      ctx.stroke()
    }
  }

  // X axis: score labels (bottom)
  ctx.textAlign = "center"
  domain.ticks.forEach((raw) => {
    ctx.fillStyle = GREY
    ctx.fillText(domain.formatTick(raw), xPos(raw), pt + ch + 18)
  })

  // Left and bottom axis lines
  ctx.beginPath()
  ctx.moveTo(pl, pt)
  ctx.lineTo(pl, pt + ch)
  ctx.strokeStyle = BORDER
  ctx.lineWidth = 1
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(pl, pt + ch)
  ctx.lineTo(pl + cw, pt + ch)
  ctx.strokeStyle = BORDER
  ctx.lineWidth = 1
  ctx.stroke()

  // Vertical bars, left-to-right stagger
  const STAGGER = 0.5
  const BAR_DUR = 1 - STAGGER

  displayDist.forEach((_d, i) => {
    if (pct[i] < 0.1) return

    const localT = Math.max(0, Math.min((progress - (i / HIST_N) * STAGGER) / BAR_DUR, 1))
    const eased = easeOutBack(localT)
    const fullBh = (pct[i] / yMax) * ch
    const bh = fullBh * eased
    const barLeft = pl + i * barSlotW

    if (i === hoverIndex) {
      ctx.fillStyle = "rgba(0,0,0,0.04)"
      ctx.fillRect(barLeft, pt, barSlotW, ch)
    }

    ctx.fillStyle = i === hoverIndex ? BAR_HOVER_COLOR : BAR_COLOR
    ctx.beginPath()
    ctx.roundRect(barLeft + GAP, pt + ch - bh, barSlotW - GAP * 2, bh, [3, 3, 0, 0])
    ctx.fill()
  })

  // Markers: vertical lines at score positions, labels in top padding
  const markAlpha = Math.max(0, Math.min((progress - 0.65) / 0.35, 1))
  if (!markAlpha) return
  ctx.save()
  ctx.globalAlpha = markAlpha

  function vmark(x: number, label: string, color: string, dashed: boolean, lxOverride?: number) {
    ctx.save()
    ctx.beginPath()
    ctx.moveTo(x, dashed ? pt + 22 : pt)
    ctx.lineTo(x, pt + ch)
    if (dashed) ctx.setLineDash([3, 4])
    ctx.strokeStyle = color
    ctx.lineWidth = dashed ? 1.5 : 2
    ctx.stroke()
    ctx.restore()
    const lx = lxOverride ?? Math.min(Math.max(x, pl + 30), pl + cw - 30)
    ctx.font = "600 11px -apple-system,system-ui,sans-serif"
    ctx.textAlign = "center"
    if (!dashed) {
      const tw = ctx.measureText(label).width
      const ph = 18,
        pp = 6,
        radius = 9
      ctx.fillStyle = color
      ctx.beginPath()
      ctx.roundRect(lx - tw / 2 - pp, pt - ph - 3, tw + pp * 2, ph, radius)
      ctx.fill()
      ctx.fillStyle = "#fff"
      ctx.fillText(label, lx, pt - 8)
    } else {
      ctx.fillStyle = color
      ctx.fillText(label, lx, pt + 14)
    }
  }

  const vousLabel = `${accountName} ${domain.formatValue(rawScore)}`
  const xVous = xPos(rawScore)
  const lxVous = Math.min(Math.max(xVous, pl + 30), pl + cw - 30)

  if (rawAvg !== null) {
    const xMoyRaw = xPos(rawAvg)
    const lxMoy = Math.min(Math.max(xMoyRaw, pl + 30), pl + cw - 30)
    vmark(xMoyRaw, ti("benchmark_q_avg_value", { value: domain.formatValue(rawAvg) }), "rgba(120,130,140,.85)", true, lxMoy)
  }

  const delta = rawAvg !== null ? parseFloat((rawScore - rawAvg).toFixed(1)) : null
  const vousColor = delta == null || delta === 0 ? "#9CA3AF" : delta > 0 ? scoreColor : "#EF4444"
  vmark(xVous, vousLabel, vousColor, false, lxVous)

  ctx.restore()
}

// ── Sub-components ──────────────────────────────────────────────────

const SKEL_BARS = [8, 15, 28, 42, 58, 72, 82, 68, 45, 22]

function mkSkelAnim() {
  return SKEL_BARS.map(() => ({
    lo: 0.45 + Math.random() * 0.25,
    hi: 0.82 + Math.random() * 0.18,
    dur: 1.1 + Math.random() * 1.4,
    delay: -(Math.random() * 2.5)
  }))
}

function HistogramSkeleton() {
  const [anim] = useState(mkSkelAnim)
  const css = anim
    .map(
      (a, i) =>
        `@keyframes hsk-b${i}{0%,100%{opacity:.35;transform:scaleY(${a.lo.toFixed(2)})}50%{opacity:.72;transform:scaleY(${a.hi.toFixed(2)})}}`
    )
    .join("\n")
  const pl = HIST_P.l,
    pr = HIST_P.r,
    pt = HIST_P.t,
    pb = HIST_P.b
  return (
    <div className="absolute inset-0 overflow-hidden">
      <style>{css}</style>
      <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <clipPath id="hsk-clip">
            <rect x={pl} y={pt} width={`calc(100% - ${pl + pr}px)`} height={`calc(100% - ${pt + pb}px)`} />
          </clipPath>
        </defs>

        {/* Y axis labels */}
        {[0, 1, 2, 3, 4].map((i) => (
          <rect
            key={i}
            x={2}
            y={`calc(100% - ${pb}px - (100% - ${pt + pb}px) * ${i / 4} - 4px)`}
            width={pl - 10}
            height={8}
            rx={3}
            fill="#E5E7EB"
          />
        ))}

        {/* Axis lines */}
        <line x1={pl} y1={pt} x2={pl} y2={`calc(100% - ${pb}px)`} stroke="#E5E7EB" strokeWidth={1} />
        <line
          x1={pl}
          y1={`calc(100% - ${pb}px)`}
          x2={`calc(100% - ${pr}px)`}
          y2={`calc(100% - ${pb}px)`}
          stroke="#E5E7EB"
          strokeWidth={1}
        />

        {/* Vertical bars */}
        {SKEL_BARS.map((pct, i) => {
          const barX = `calc(${pl + 2}px + (100% - ${pl + pr}px) * ${(i / HIST_N).toFixed(4)})`
          const barW = `calc((100% - ${pl + pr}px) * ${(0.8 / HIST_N).toFixed(4)})`
          const barH = `calc((100% - ${pt + pb}px) * ${(pct / 100).toFixed(4)})`
          const barY = `calc(100% - ${pb}px - (100% - ${pt + pb}px) * ${(pct / 100).toFixed(4)})`
          return (
            <rect
              key={i}
              x={barX}
              y={barY}
              width={barW}
              height={barH}
              rx={3}
              fill="#E5E7EB"
              style={{
                transformBox: "fill-box",
                transformOrigin: "50% 100%",
                animation: `hsk-b${i} ${anim[i].dur.toFixed(2)}s ease-in-out infinite`,
                animationDelay: `${anim[i].delay.toFixed(2)}s`
              }}
            />
          )
        })}

        {/* Shimmer sweep */}
        <rect
          x={pl}
          y={pt}
          width={`calc(100% - ${pl + pr}px)`}
          height={`calc(100% - ${pt + pb}px)`}
          fill="url(#hsk-grad)"
          clipPath="url(#hsk-clip)"
        />
        <defs>
          <linearGradient id="hsk-grad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="white" stopOpacity="0" />
            <stop offset="50%" stopColor="white" stopOpacity="0.5" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
            <animateTransform
              attributeName="gradientTransform"
              type="translate"
              from="-1 0"
              to="2 0"
              dur="1.4s"
              repeatCount="indefinite"
            />
          </linearGradient>
        </defs>

        {/* X axis labels */}
        {[0, 2, 4, 6, 8, 10].map((v) => (
          <rect
            key={v}
            x={`calc(${pl}px + (100% - ${pl + pr}px) * ${(v / 10).toFixed(2)} - 8px)`}
            y={`calc(100% - ${pb - 8}px)`}
            width={16}
            height={8}
            rx={3}
            fill="#E5E7EB"
          />
        ))}
      </svg>
    </div>
  )
}

function DeltaChip({ delta, decimals }: { delta: number; decimals: number }) {
  return (
    <div className="[&_svg]:mr-0 flex-shrink-0">
      <Chip
        isOutlineColored
        icon={delta < -0.3 ? faArrowDown : delta > 0.3 ? faArrowUp : faEquals}
        color={delta < -0.3 ? "#eb5a46" : delta > 0.3 ? "#20CA7E" : "#9CA3AF"}>
        {(delta > 0 ? "+" : "") + delta.toFixed(decimals)}
      </Chip>
    </div>
  )
}

// ── Main component ──────────────────────────────────────────────────

const distCache = new Map<string, BenchmarkDistribution>()

export default function DashboardBenchmarkDetailWidget({
  accountName,
  accountOptions,
  questionAid,
  benchmarkEntry,
  score,
  sector,
  navItems,
  navCurrentIndex,
  onNavigate,
  domain = "question",
  distributionOverride
}: OwnProps) {
  const { t } = useTranslation()
  const histDomain = domain === "nps" ? NPS_DOMAIN : QUESTION_DOMAIN
  const deltaDecimals = domain === "nps" ? 0 : 1
  const dispatch = useDispatch<any>()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const canvasContainerRef = useRef<HTMLDivElement>(null)
  // Un-rotated ancestor of the flip card, used only to size the canvas: canvasContainerRef sits
  // inside the rotateY(...) wrapper, so its getBoundingClientRect() is foreshortened by the 3D
  // perspective mid-flip — sizing off that produced a visible dimension "jump" on every flip.
  const flipOuterRef = useRef<HTMLDivElement>(null)
  const hoverIndexRef = useRef<number | null>(null)
  const activeNavRef = useRef<HTMLDivElement>(null)

  const [distribution, setDistribution] = useState<BenchmarkDistribution | null>(null)
  const [distLoading, setDistLoading] = useState(false)
  const [flipped, setFlipped] = useState(false)
  const flipLockRef = useRef(false)
  const canFlip = !!(navItems && navItems.length > 1 && onNavigate)
  // The NPS dashboard modal (single navItem, so canFlip is always false) still resolves a real
  // sector server-side (getNps() returns the account's own sector, falling back to 'all' only
  // when no sector-specific row exists — resolveNpsBenchmark in benchmarkQuestion.utils.ts) but
  // never showed which one, since the sector block lived inside the flip-only right panel (Tom,
  // 2026-09-22). Widened the panel's own condition rather than moving the sector block out on its
  // own, so the histogram's width class below (flex2 vs flex-1) stays in sync with whether this
  // panel is actually showing.
  const showRightPanel = canFlip || !!sector
  const [hoverTooltip, setHoverTooltip] = useState<{
    index: number
    rangeLabel: string
    pct: number
    n: number
    x: number
    y: number
  } | null>(null)

  useEffect(() => {
    if (distributionOverride !== undefined) {
      setDistribution(distributionOverride)
      setDistLoading(false)
      return
    }
    if (!questionAid) return
    const key = `${questionAid}|${sector}`
    const cached = distCache.get(key)
    if (cached) {
      setDistribution(cached)
      setDistLoading(false)
      return
    }
    setDistLoading(true)
    dispatch(fetchBenchmarkQuestionDistribution({ questionAid, sector }))
      .then((action: any) => {
        if (action.payload?.total) {
          distCache.set(key, action.payload)
          setDistribution(action.payload)
        } else {
          setDistribution(null)
        }
      })
      .finally(() => setDistLoading(false))
  }, [questionAid, sector, distributionOverride]) // eslint-disable-line react-hooks/exhaustive-deps

  const rawScore = score
  // NPS is -100..100 rather than 0-100, so remap it onto the 0-100 scale benchmarkColor expects
  // (0 → 50, unchanged thresholds) instead of duplicating the color-threshold logic per domain.
  const scoreColor = benchmarkColor(domain === "nps" ? (score + 100) / 2 : score, accountOptions)

  const rawBenchmark = benchmarkEntry ? benchmarkEntry.value : null

  const dist = useMemo(
    () => (distribution ? distribution.buckets.map((b) => ({ v: b.min, n: b.count })) : null),
    [distribution]
  )

  const animProgressRef = useRef(1)
  const animFrameRef = useRef<number | null>(null)
  const hasAnimatedRef = useRef(false)

  const drawCanvas = useCallback(
    (progress = animProgressRef.current) => {
      const sizeEl = flipOuterRef.current
      const canvas = canvasRef.current
      if (!sizeEl || !canvas) return
      const { width, height } = sizeEl.getBoundingClientRect()
      if (width <= 0 || height <= 0) return
      const dpr = window.devicePixelRatio || 1
      canvas.width = width * dpr
      canvas.height = height * dpr
      canvas.style.width = width + "px"
      canvas.style.height = height + "px"
      const ctx = canvas.getContext("2d")!
      ctx.scale(dpr, dpr)
      if (distLoading) {
        ctx.clearRect(0, 0, width, height)
        return
      }
      paintHistogram(
        ctx,
        width,
        height,
        rawScore,
        scoreColor,
        dist ?? [],
        rawBenchmark,
        progress,
        hoverIndexRef.current,
        accountName,
        histDomain
      )
    },
    [rawScore, scoreColor, dist, rawBenchmark, distLoading, accountName, histDomain]
  ) // eslint-disable-line react-hooks/exhaustive-deps

  // Hover: map mouse X → bar index, trigger redraw + tooltip
  useEffect(() => {
    const canvas = canvasRef.current
    const container = canvasContainerRef.current
    if (!canvas || !container) return

    function onMouseMove(e: MouseEvent) {
      const rect = canvas!.getBoundingClientRect()
      const containerRect = container!.getBoundingClientRect()
      const cw = rect.width - HIST_P.l - HIST_P.r
      const relX = e.clientX - rect.left - HIST_P.l
      if (relX < 0 || relX > cw || !dist) {
        if (hoverIndexRef.current !== null) {
          hoverIndexRef.current = null
          setHoverTooltip(null)
          drawCanvas()
        }
        return
      }
      const i = Math.min(Math.max(Math.floor((relX / cw) * HIST_N), 0), HIST_N - 1)
      if (hoverIndexRef.current !== i) {
        hoverIndexRef.current = i
        const displayDist = expandToFixed(dist, HIST_N, histDomain)
        const smoothed = gaussianSmooth(displayDist.map((d) => d.n))
        const total = smoothed.reduce((a, b) => a + b, 0)
        const pct = total > 0 ? (smoothed[i] / total) * 100 : 0
        const barCenter = containerRect.left + HIST_P.l + (i + 0.5) * (cw / HIST_N) - containerRect.left
        const rawN = Math.round((displayDist[i]?.n ?? 0) * 10) / 10
        const span = histDomain.max - histDomain.min
        const bucketStart = histDomain.min + (i / HIST_N) * span
        const bucketEnd = histDomain.min + ((i + 1) / HIST_N) * span
        const rangeLabel = `${histDomain.formatTick(bucketStart)} – ${histDomain.formatTick(bucketEnd)}`
        setHoverTooltip({ index: i, rangeLabel, pct, n: rawN, x: barCenter, y: e.clientY - containerRect.top })
        drawCanvas()
      }
    }

    function onMouseLeave() {
      if (hoverIndexRef.current !== null) {
        hoverIndexRef.current = null
        setHoverTooltip(null)
        drawCanvas()
      }
    }

    canvas.addEventListener("mousemove", onMouseMove)
    canvas.addEventListener("mouseleave", onMouseLeave)
    return () => {
      canvas.removeEventListener("mousemove", onMouseMove)
      canvas.removeEventListener("mouseleave", onMouseLeave)
    }
  }, [drawCanvas, dist, histDomain]) // eslint-disable-line react-hooks/exhaustive-deps

  // Trigger entrance animation on first load; redraw instantly on navigation
  useEffect(() => {
    if (!dist) return
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current)
    if (hasAnimatedRef.current) {
      animProgressRef.current = 1
      drawCanvas(1)
      return
    }
    hasAnimatedRef.current = true
    animProgressRef.current = 0
    const start = performance.now()
    const DURATION = 750
    function tick() {
      const p = Math.min((performance.now() - start) / DURATION, 1)
      animProgressRef.current = p
      drawCanvas(p)
      if (p < 1) animFrameRef.current = requestAnimationFrame(tick)
    }
    animFrameRef.current = requestAnimationFrame(tick)
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current)
    }
  }, [dist]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    activeNavRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }, [navCurrentIndex])

  useEffect(() => {
    const ro = new ResizeObserver(() => requestAnimationFrame(() => drawCanvas()))
    if (canvasContainerRef.current) ro.observe(canvasContainerRef.current)
    const timer = setTimeout(() => drawCanvas(), 50)
    return () => {
      ro.disconnect()
      clearTimeout(timer)
    }
  }, [drawCanvas])

  useEffect(() => {
    if (!flipped) {
      const t = setTimeout(() => drawCanvas(), 580)
      return () => clearTimeout(t)
    }
  }, [flipped, drawCanvas])

  return (
    <div className="flex flex-col">
      {/* Full-width active question row */}
      {navItems &&
        navCurrentIndex != null &&
        (() => {
          const active = navItems[navCurrentIndex]
          return active ? (
            <div className="relative mx-[40px] mb-14 bg-gray-100 rounded-lg overflow-hidden">
              <div
                className="absolute left-0 top-0 bottom-0 rounded-sm"
                style={{ width: 3, backgroundColor: active.axisColor }}
              />
              <ListItem isSmall isEditable onClick={() => {}} className="cursor-default">
                <div className="pl-2 flex items-center justify-between gap-4 flex-1 min-w-0">
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <SurveyQuestionLabel label={active.label} topicLabel={active.topicLabel} axisLabel={active.axisLabel} color={active.axisColor} />
                  </div>
                  {typeof active.delta === "number" && !isNaN(active.delta) && (
                    <DeltaChip delta={active.delta} decimals={deltaDecimals} />
                  )}
                </div>
              </ListItem>
            </div>
          ) : null
        })()}

      {/* Histogram + right panel */}
      <div className="flex px-[40px] gap-8 pb-4">
        {/* Left: flip card — front = histogram, back = question list */}
        <div ref={flipOuterRef} className={showRightPanel ? "flex2 min-w-0" : "flex-1 min-w-0"} style={{ perspective: "1400px" }}>
          <div
            style={{
              transformStyle: "preserve-3d",
              transition: "transform 0.55s cubic-bezier(0.4,0,0.2,1)",
              transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
              position: "relative",
              width: "100%",
              height: 280
            }}>
            {/* Front: histogram */}
            <div
              style={{
                backfaceVisibility: "hidden",
                WebkitBackfaceVisibility: "hidden",
                position: "absolute",
                inset: 0
              }}>
              <div ref={canvasContainerRef} className="absolute inset-0">
                <canvas ref={canvasRef} className="absolute inset-0" />
                {distLoading && <HistogramSkeleton />}
                {hoverTooltip && hoverTooltip.pct >= 0.1 && (
                  <div
                    className="absolute pointer-events-none bg-white rounded-lg shadow-md border border-gray-100 px-3 py-1.5 z-10"
                    style={{
                      top: Math.max(4, hoverTooltip.y - 56),
                      left: hoverTooltip.x,
                      whiteSpace: "nowrap",
                      transform:
                        hoverTooltip.index <= 1
                          ? "translateX(0%)"
                          : hoverTooltip.index >= HIST_N - 2
                            ? "translateX(-100%)"
                            : "translateX(-50%)"
                    }}>
                    <span className="font-semibold">{hoverTooltip.rangeLabel}</span>
                    <span className="grey-t ml-2">
                      {hoverTooltip.pct.toFixed(1)}
                      {t("benchmark_q_pct_companies")}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Back: question list */}
            <div
              style={{
                backfaceVisibility: "hidden",
                WebkitBackfaceVisibility: "hidden",
                transform: "rotateY(180deg)",
                position: "absolute",
                inset: 0
              }}>
              <div
                className="[&::-webkit-scrollbar]:hidden"
                style={{ position: "absolute", inset: 0, overflowY: "auto", scrollbarWidth: "none" }}>
                {navItems &&
                  onNavigate &&
                  navCurrentIndex != null &&
                  navItems.map((item, i) => {
                    if (i === navCurrentIndex) return null
                    return (
                      <div key={item.questionAid} className="relative">
                        <ListItem
                          isSmall
                          isEditable
                          className="!px-4"
                          onClick={() => {
                            if (flipLockRef.current) return
                            flipLockRef.current = true
                            onNavigate(i)
                            setFlipped(false)
                            setTimeout(() => {
                              flipLockRef.current = false
                            }, 600)
                          }}>
                          <div className="pl-2 flex items-center justify-between gap-4 flex-1 min-w-0">
                            <div className="flex-1 min-w-0 overflow-hidden">
                              <SurveyQuestionLabel label={item.label} topicLabel={item.topicLabel} axisLabel={item.axisLabel} color={item.axisColor} />
                            </div>
                            {typeof item.delta === "number" && !isNaN(item.delta) && (
                              <DeltaChip delta={item.delta} decimals={deltaDecimals} />
                            )}
                          </div>
                        </ListItem>
                      </div>
                    )
                  })}
              </div>
            </div>
          </div>
        </div>

        {/* Right: sector info + flip button — hidden when there's neither (full-width histogram instead) */}
        {showRightPanel && (
          <div className="flex1 min-w-0 overflow-hidden flex flex-col justify-center gap-3 pl-6 border-l border-gray-100">
            {sector && (
              <div className="flex justify-center items-center gap-4">
                <div className="flex flex-col gap-0.5">
                  <span className="grey-t">{t("benchmark_title_sector")}</span>
                  <span className="font-bold grey-t">{t("benchmark_sector_" + sector)}</span>
                </div>
                {/* Only for a real, specific sector — not the "all" fallback (Tom, 2026-09-22) */}
                {sector !== "all" && (
                  <img
                    src={require("@/assets/owl-thinking.png")}
                    alt="owl"
                    style={{ width: 72, height: 72, objectFit: "contain", flexShrink: 0 }}
                  />
                )}
              </div>
            )}
            {canFlip && (
              <Button
                onClick={() => {
                  if (flipLockRef.current) return
                  flipLockRef.current = true
                  setFlipped((f: boolean) => !f)
                  setTimeout(() => {
                    flipLockRef.current = false
                  }, 600)
                }}
                isWithBorder
                isFullWidth
                icon={flipped ? faChevronLeft : faList}>
                {flipped ? t("utils_back") : t("benchmark_q_see_questions")}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
